/**
 * ScheduleMessageButton
 * -----------------------------------------------------------------------------
 * Botão para abrir o ScheduleMessageDialog. Pode ser renderizado em dois modos:
 *   1) Composer (default) – ícone simples, sem badge; sempre abre criação.
 *   2) Header (showCount=true) – ícone + badge com a contagem de mensagens
 *      agendadas pendentes/com erro vinculadas à conversa atual. Decide
 *      dinamicamente entre:
 *        • 0 itens → abre criação
 *        • 1+ itens → abre o modal `ConversationScheduledMessagesModal`
 *                     com a listagem (lá o usuário pode criar nova ou clicar
 *                     em uma linha para editar).
 *
 * @version 1.3.0
 * RELEASE: 2026-06-02 v1.3.0 - Unificado: ≥1 item sempre abre o modal de
 *          listagem (removido atalho de edição direta para 1 item).
 * RELEASE: 2026-06-02 v1.2.0 - Decisão dinâmica no clique (criar/editar/listar).
 * RELEASE: 2026-06-02 v1.1.0 - Modo `showCount` com badge.
 * RELEASE: 2026-06-02 v1.0.0 - Criação do botão de agendamento no composer.
 */


import { useEffect, useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AlarmClock } from 'lucide-react';
import { ScheduleMessageDialog } from './ScheduleMessageDialog';
import { ConversationScheduledMessagesModal } from './scheduled/ConversationScheduledMessagesModal';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';


interface ScheduleMessageButtonProps {
  conversationId?: string;
  disabled?: boolean;
  /** Contexto da inbox para alimentar o badge do canal e o preview. */
  conversationContext?: {
    inboxId?: string;
    inboxName?: string;
    channelType?: string;
    /** v2.2.0 — última inbound (janela 24h WABA). */
    lastInboundAt?: string | null;
  };
  /** Notifica o composer que um agendamento foi criado (refresh contador). */
  onScheduled?: (id: string) => void;
  /** Quando true, exibe contador (badge) e usa tamanho/estilo do header. */
  showCount?: boolean;
}

interface ScheduledCount {
  pending_count: number;
  failed_count: number;
  total_count: number;
}

export function ScheduleMessageButton({
  conversationId,
  disabled,
  conversationContext,
  onScheduled,
  showCount = false,
}: ScheduleMessageButtonProps) {
  // Estados de abertura — separados por modo
  const [openCreate, setOpenCreate] = useState(false);
  const [openListModal, setOpenListModal] = useState(false);

  const { canCreate, isAdmin } = usePermissions();
  const qc = useQueryClient();

  // ID único por instância — evita colisão de canal Realtime quando o mesmo
  // botão é montado em mais de um lugar (ex.: página /tenant/chat + modal
  // ChatConversationModal sobreposto via DealDetailsModalPremium).
  const instanceId = useId();


  // ---------------------------------------------------------------------------
  // Contagem de agendamentos pendentes/com erro para esta conversa
  // ---------------------------------------------------------------------------
  const { data: counts } = useQuery<ScheduledCount>({
    queryKey: ['scheduled-messages-count', conversationId],
    enabled: showCount && !!conversationId,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_scheduled_messages_count_by_conversation' as never,
        { p_conversation_id: conversationId } as never,
      );
      if (error) throw error;
      const list = (data ?? []) as ScheduledCount[] | ScheduledCount | null;
      const row = Array.isArray(list) ? list[0] : list;
      return {
        pending_count: Number(row?.pending_count ?? 0),
        failed_count: Number(row?.failed_count ?? 0),
        total_count: Number(row?.total_count ?? 0),
      };
    },
  });

  // ---------------------------------------------------------------------------
  // Realtime: invalida o contador quando reminder_queue muda para esta conversa
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!showCount || !conversationId) return;
    const channel = supabase
      .channel(`scheduled-count-${conversationId}-${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reminder_queue',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          qc.invalidateQueries({
            queryKey: ['scheduled-messages-count', conversationId],
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, showCount, qc]);

  // Sem permissão de criação → não renderiza o botão
  if (!isAdmin && !canCreate('scheduled_messages')) return null;

  const total = counts?.total_count ?? 0;
  const hasFailed = (counts?.failed_count ?? 0) > 0;
  const hasItems = total > 0;
  const badgeLabel = total > 9 ? '9+' : String(total);

  // Cor do ícone
  const iconColorClass = hasFailed
    ? 'text-destructive'
    : hasItems
      ? 'text-amber-600 dark:text-amber-500'
      : 'text-foreground hover:text-primary';

  const tooltipText = !hasItems
    ? 'Agendar mensagem'
    : hasFailed
      ? `${total} agendamento(s) — contém erro`
      : `${total} mensagem(ns) agendada(s)`;

  const sizeClass = showCount ? 'h-11 w-11' : 'h-8 w-8';

  // ---------------------------------------------------------------------------
  // Click handler — unificado
  // 0 itens → criação direta; ≥1 → modal de listagem.
  // ---------------------------------------------------------------------------
  const handleClick = () => {
    if (!showCount || !conversationId || !hasItems) {
      setOpenCreate(true);
      return;
    }
    setOpenListModal(true);
  };

  const invalidateAll = () => {
    qc.invalidateQueries({
      queryKey: ['scheduled-messages-count', conversationId],
    });
    qc.invalidateQueries({
      queryKey: ['scheduled-messages-by-conversation', conversationId],
    });
    qc.invalidateQueries({ queryKey: ['scheduled-messages'] });
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('relative', sizeClass, iconColorClass)}
            disabled={disabled || !conversationId}
            onClick={handleClick}
            aria-label="Agendar mensagem"
          >
            <AlarmClock className={cn(showCount ? 'h-5 w-5' : 'h-4 w-4')} />
            {showCount && hasItems && (
              <span
                className={cn(
                  'absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full',
                  'flex items-center justify-center text-[10px] font-semibold leading-none',
                  hasFailed
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-amber-500 text-white',
                )}
              >
                {badgeLabel}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>

      {/* Modo criação (0 itens ou composer) */}
      <ScheduleMessageDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        conversationId={conversationId}
        conversationContext={conversationContext}
        onSuccess={(id) => {
          invalidateAll();
          onScheduled?.(id);
        }}
      />

      {/* Modo listagem (≥1 item) — criar nova ou clicar em linha para editar */}
      {conversationId && (
        <ConversationScheduledMessagesModal
          open={openListModal}
          onOpenChange={setOpenListModal}
          conversationId={conversationId}
          conversationContext={conversationContext}
        />
      )}
    </>
  );
}

